import { Injectable, TemplateRef } from '@angular/core';
import { ActiveToast, IndividualConfig, ToastrService } from 'ngx-toastr';

export interface AppToast {
  toastId: number;
  activeToast: ActiveToast<any>;
}

export interface AppToastOptions {
  classname?: string;
  header?: string;
  delay?: number;
  autohide?: boolean;
  showSpinner?: boolean;
}

@Injectable({ providedIn: 'root' })
export class ToastService {
  constructor(private toastrService: ToastrService) { }

  show(textOrTpl: string | TemplateRef<any>, options: AppToastOptions = {}): AppToast {
    const activeToast = this.createToast(textOrTpl, options);

    return {
      toastId: activeToast.toastId,
      activeToast,
    };
  }

  showSuccess(textOrTpl: string | TemplateRef<any>, options: Omit<AppToastOptions, 'classname'> = {}): AppToast {
    return this.show(textOrTpl, { ...options, classname: 'bg-success-subtle text-dark' });
  }

  showError(textOrTpl: string | TemplateRef<any>, options: Omit<AppToastOptions, 'classname'> = {}): AppToast {
    return this.show(textOrTpl, { ...options, classname: 'bg-danger-subtle text-dark' });
  }

  showInfo(textOrTpl: string | TemplateRef<any>, options: Omit<AppToastOptions, 'classname'> = {}): AppToast {
    return this.show(textOrTpl, { ...options, classname: 'bg-primary-subtle text-dark' });
  }

  showWarning(textOrTpl: string | TemplateRef<any>, options: Omit<AppToastOptions, 'classname'> = {}): AppToast {
    return this.show(textOrTpl, { ...options, classname: 'bg-warning-subtle text-dark' });
  }

  showProgress(textOrTpl: string | TemplateRef<any>, header?: string): AppToast {
    return this.show(textOrTpl, { classname: 'bg-primary-subtle text-dark', autohide: false, showSpinner: true, header });
  }

  remove(toast: AppToast) {
    this.toastrService.remove(toast.toastId);
  }

  clear() {
    this.toastrService.clear();
  }

  private createToast(textOrTpl: string | TemplateRef<any>, options: AppToastOptions): ActiveToast<any> {
    const message = typeof textOrTpl === 'string' ? textOrTpl : '';
    const config = this.buildConfig(options);

    if (options.showSpinner) {
      return this.toastrService.info(message, options.header, {
        ...config,
        toastClass: `${config.toastClass ?? ''} toast-loading`.trim(),
      });
    }

    switch (this.getToastKind(options.classname)) {
      case 'success':
        return this.toastrService.success(message, options.header, config);
      case 'error':
        return this.toastrService.error(message, options.header, config);
      case 'warning':
        return this.toastrService.warning(message, options.header, config);
      default:
        return this.toastrService.info(message, options.header, config);
    }
  }

  private buildConfig(options: AppToastOptions): Partial<IndividualConfig> {
    const timeOut = options.delay ?? 5000;

    return {
      closeButton: true,
      disableTimeOut: options.autohide === false,
      messageClass: 'app-toast-message',
      tapToDismiss: false,
      timeOut,
      titleClass: 'app-toast-title',
      toastClass: 'ngx-toastr app-toast',
      extendedTimeOut: timeOut,
    };
  }

  private getToastKind(classname?: string): 'success' | 'error' | 'warning' | 'info' {
    if (classname?.includes('bg-success')) {
      return 'success';
    }

    if (classname?.includes('bg-danger')) {
      return 'error';
    }

    if (classname?.includes('bg-warning')) {
      return 'warning';
    }

    return 'info';
  }
}
